/**
 * Composable useRotation360 - Gestion de la rotation 360° par drag/swipe
 * Basé sur l'analyse de ConfigView.js
 */

import { ref, type Ref, unref, type MaybeRef } from 'vue'

/**
 * Options de configuration du composable
 */
export interface UseRotation360Options {
  /** Nombre d'angles affichables (positions de rotation) */
  numAngleStep: number
  /** Callback appelé lors du changement d'angle */
  onAngleChange?: (angle: number) => void
  /** Désactiver les interactions (peut être un Ref réactif) */
  disabled?: MaybeRef<boolean>
}

/**
 * Valeur de retour du composable
 */
export interface UseRotation360Return {
  /** Angle actuel (0 à numAngleStep-1) */
  currentAngle: Ref<number>
  /** Indique si une rotation est en cours */
  isRotating: Ref<boolean>
  /** Définir l'angle directement */
  setAngle: (angle: number) => void
  /** Passer à l'angle suivant */
  nextAngle: () => void
  /** Passer à l'angle précédent */
  prevAngle: () => void
  /** Attacher les événements à un élément */
  bindEvents: (element: HTMLElement) => void
  /** Détacher les événements */
  unbindEvents: () => void
}

/**
 * Composable pour gérer la rotation 360° interactive
 * 
 * @param options - Configuration du composable
 * @returns Interface de gestion de la rotation
 * 
 * @example
 * ```typescript
 * const { currentAngle, isRotating, bindEvents } = useRotation360({
 *   numAngleStep: 12,
 *   onAngleChange: (angle) => console.log('Angle:', angle)
 * })
 * 
 * onMounted(() => {
 *   const element = viewerRef.value
 *   if (element) bindEvents(element)
 * })
 * ```
 */
export function useRotation360(options: UseRotation360Options): UseRotation360Return {
  const { numAngleStep, onAngleChange, disabled = false } = options

  // États réactifs
  const currentAngle = ref<number>(0)
  const isRotating = ref<boolean>(false)

  // Variables pour le suivi du drag/swipe
  let targetElement: HTMLElement | null = null
  let step = 0
  let stepX = 0
  let initialAngle = 0

  /**
   * Définir l'angle actuel (avec validation)
   */
  const setAngle = (angle: number): void => {
    // Normaliser l'angle dans la plage [0, numAngleStep-1]
    let normalizedAngle = angle % numAngleStep
    if (normalizedAngle < 0) {
      normalizedAngle += numAngleStep
    }

    if (currentAngle.value !== normalizedAngle) {
      currentAngle.value = normalizedAngle
      onAngleChange?.(normalizedAngle)
    }
  }

  /**
   * Passer à l'angle suivant (rotation circulaire)
   */
  const nextAngle = (): void => {
    setAngle((currentAngle.value + 1) % numAngleStep)
  }

  /**
   * Passer à l'angle précédent (rotation circulaire)
   */
  const prevAngle = (): void => {
    setAngle((currentAngle.value - 1 + numAngleStep) % numAngleStep)
  }

  /**
   * Gestionnaire mousedown/touchstart
   */
  const handleStart = (event: MouseEvent | TouchEvent): void => {
    // Ignorer si désactivé
    if (unref(disabled)) {
      return
    }

    event.preventDefault()

    isRotating.value = true
    initialAngle = currentAngle.value

    // Calculer stepX basé sur la largeur de l'élément
    if (targetElement) {
      stepX = targetElement.offsetWidth / numAngleStep
    }

    // Obtenir la position X initiale
    const pageX = event instanceof MouseEvent 
      ? event.pageX 
      : event.touches[0].pageX

    step = Math.round(pageX / stepX)

    // Attacher les événements move et end
    if (targetElement) {
      targetElement.addEventListener('mousemove', handleMove as EventListener)
      targetElement.addEventListener('touchmove', handleMove as EventListener)
      document.addEventListener('mouseup', handleEnd)
      document.addEventListener('touchend', handleEnd)
    }
  }

  /**
   * Gestionnaire mousemove/touchmove
   */
  const handleMove = (event: MouseEvent | TouchEvent): void => {
    event.preventDefault()

    // Obtenir la position X actuelle
    const pageX = event instanceof MouseEvent
      ? event.pageX
      : event.touches[0].pageX

    const newStep = Math.round(pageX / stepX)

    // Calculer et appliquer le changement d'angle
    if (step < newStep && newStep !== 0) {
      // Rotation vers la droite
      initialAngle = (initialAngle + 1) % numAngleStep
      setAngle(initialAngle)
    } else if (newStep < step && newStep !== 0) {
      // Rotation vers la gauche
      initialAngle = (initialAngle - 1 + numAngleStep) % numAngleStep
      setAngle(initialAngle)
    }

    step = newStep
  }

  /**
   * Gestionnaire mouseup/touchend
   */
  const handleEnd = (): void => {
    isRotating.value = false

    // Détacher les événements move et end
    if (targetElement) {
      targetElement.removeEventListener('mousemove', handleMove as EventListener)
      targetElement.removeEventListener('touchmove', handleMove as EventListener)
    }
    document.removeEventListener('mouseup', handleEnd)
    document.removeEventListener('touchend', handleEnd)

    // Réinitialiser les variables de suivi
    step = 0
    stepX = 0
  }

  /**
   * Gestionnaire de navigation au clavier
   */
  const handleKeyDown = (event: KeyboardEvent): void => {
    // Ignorer si désactivé
    if (unref(disabled)) {
      return
    }

    if (event.key === 'ArrowLeft') {
      prevAngle()
      event.preventDefault()
    } else if (event.key === 'ArrowRight') {
      nextAngle()
      event.preventDefault()
    }
  }

  /**
   * Attacher les événements à un élément
   */
  const bindEvents = (element: HTMLElement): void => {
    targetElement = element

    // Rendre l'élément focusable pour la navigation au clavier
    if (!element.hasAttribute('tabindex')) {
      element.setAttribute('tabindex', '0')
    }

    // Attacher les événements de début
    element.addEventListener('mousedown', handleStart as EventListener)
    element.addEventListener('touchstart', handleStart as EventListener, { passive: false })
    
    // Attacher les événements clavier
    element.addEventListener('keydown', handleKeyDown as EventListener)
  }

  /**
   * Détacher tous les événements
   */
  const unbindEvents = (): void => {
    if (targetElement) {
      // Détacher les événements de début
      targetElement.removeEventListener('mousedown', handleStart as EventListener)
      targetElement.removeEventListener('touchstart', handleStart as EventListener)

      // Détacher les événements move et end (au cas où)
      targetElement.removeEventListener('mousemove', handleMove as EventListener)
      targetElement.removeEventListener('touchmove', handleMove as EventListener)
      
      // Détacher les événements clavier
      targetElement.removeEventListener('keydown', handleKeyDown as EventListener)
    }
    
    document.removeEventListener('mouseup', handleEnd)
    document.removeEventListener('touchend', handleEnd)

    targetElement = null
    isRotating.value = false
  }

  return {
    currentAngle,
    isRotating,
    setAngle,
    nextAngle,
    prevAngle,
    bindEvents,
    unbindEvents
  }
}